/**
 * ZPipedream - Zod schemas for the Pipedream API (/api/pipedream/*)
 *
 * Controller: pipedream/pipedream.controller.ts
 *
 * Covers the connect-flow surface the frontend uses: fetching a connect token,
 * listing/deleting user triggers, and creating a tool credential from a
 * connected Pipedream account. The remaining controller routes (connect,
 * get_app_list, component definition, app search/update) are not consumed by
 * the frontend service and are left on the legacy path.
 */

import { z } from "zod";

import type { DbEntity } from "../../../client";
import type {
  PipedreamConnectToken,
  PipedreamTrigger
} from "../../../shared/domain/tools/pipedream.types";
import { ApiSuccessSchema } from "../../common/response.schema";

const DeleteTriggerPathVarsSchema = z.object({ triggerId: z.string() });

/**
 * Create body. The service reads exactly these fields (nameSlug, external_user_id,
 * data.account_id, name) — this closed schema doubles as the mass-assignment guard.
 */
const CreatePipedreamCredentialBodySchema = z.object({
  data: z.object({ account_id: z.string() }),
  nameSlug: z.string(),
  external_user_id: z.string(),
  // The frontend sends `type: "PIPEDREAM"` but the service never reads it (and
  // it is absent from the documented required fields) — optional so a valid
  // request is never rejected on it.
  type: z.string().optional(),
  name: z.string()
});

/**
 * The listing wire shape — the Pipedream SDK's `GetTriggersResponse` (a
 * `{ data: PipedreamTrigger[] }` envelope) or null when no client is configured.
 */
type GetTriggersResult = { data: PipedreamTrigger[] } | null;

/**
 * Created credential projection (mirrors CREATED_TOOL_CREDENTIAL_SELECT in the
 * backend's pipedream-credential.service.ts). `null` = no AgentExternalTool
 * matched the nameSlug.
 */
type CreatedPipedreamCredential =
  | (Pick<DbEntity.ToolCredentials, "id" | "name" | "type" | "status" | "createdAt"> & {
      nodeCredentials: DbEntity.NodeCredentials | null;
    })
  | null;

export const ZPipedream = {
  /** POST /api/pipedream/getPipedreamToken — issue a Pipedream connect token for the org. */
  GetPipedreamToken: {
    method: "POST" as const,
    path: "/api/pipedream/getPipedreamToken",
    Response: ApiSuccessSchema(z.custom<PipedreamConnectToken>())
  },

  /** POST /api/pipedream/getTriggersOfUser — list the org's configured triggers. */
  GetTriggersOfUser: {
    method: "POST" as const,
    path: "/api/pipedream/getTriggersOfUser",
    Response: ApiSuccessSchema(z.custom<GetTriggersResult>())
  },

  /** POST /api/pipedream/deleteTrigger/:triggerId — delete a trigger; true on success. */
  DeleteTrigger: {
    method: "POST" as const,
    path: "/api/pipedream/deleteTrigger/:triggerId",
    PathVars: DeleteTriggerPathVarsSchema,
    Response: ApiSuccessSchema(z.boolean())
  },

  /** POST /api/pipedream/createPipedreamCredential — create a credential from a connected account. */
  CreatePipedreamCredential: {
    method: "POST" as const,
    path: "/api/pipedream/createPipedreamCredential",
    Body: CreatePipedreamCredentialBodySchema,
    Response: ApiSuccessSchema(z.custom<CreatedPipedreamCredential>())
  }
} as const;
